import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Alert, Box, ButtonGroup, Card, FormHelp, Heading, Modal, Paragraph, Text } from '@zillow/constellation-10';

import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import faqs from '@renter-hub/constants/FaqConstants';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import NotificationContainer from '@common/components/NotificationContainer';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import { PaymentAuthorizationTerms } from '@common/components/PaymentAuthorizationTerms/PaymentAuthorizationTerms';
import userPersonas from '@common/constants/userPersonas';
import type { RenterTransactionProps } from '@renter-hub/models/Types';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import type { CreatePaymentSubscriptionWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import type { RootState } from '../../../../store/types';
import AvailablePaymentMethods from '../../components/AvailablePaymentMethods';

interface Props {
  today: Date;
  transaction: RenterTransactionProps;
  failedPaymentEmail: boolean;
  onSubmit: (rentPaymentInfo: CreatePaymentSubscriptionWebApiRequestDto) => any;
  goToDashboard: () => void;
}

export const PayPastDueRentPaymentPage = (props: Props) => {
  const paymentMethods = useSelector((state: RootState) => state.payments.renter.dashboard.paymentMethods);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(() => {
    if (!paymentMethods?.length) return '';

    // Get verified payment methods using the shared utility
    const verifiedPaymentMethods = getVerifiedPaymentMethods(paymentMethods);

    // Use the first verified payment method
    return verifiedPaymentMethods[0]?.id || '';
  });

  const { today, transaction, failedPaymentEmail } = props;

  const goToDashboard = () => {
    props.goToDashboard();
  };

  const onSubmit = async () => {
    setIsLoading(true);

    const response = await props.onSubmit({
      scheduleMonth: transaction.scheduleMonth,
      // Normalize to midnight UTC via YYYY-MM-DD round-trip to match the date-only
      // timestamp format the backend expects (consistent with other payment flows)
      paymentDate: new Date().setHours(0, 0, 0, 0),
      recurring: false,
      encodedPaymentMethodId: selectedPaymentMethodId,
    });

    const { error } = response;
    if (error) {
      setIsLoading(false);
    }
  };

  const renderOverdueAlert = () =>
    failedPaymentEmail ? (
      <Alert tone="critical">
        We couldn’t process your payment using your payment method. Contact your bank, or try a different payment
        method.
      </Alert>
    ) : (
      <Alert tone="critical">Your rent payment is overdue</Alert>
    );

  const renderRentPaymentSection = () => {
    const showCardFee = transaction.zgFee !== 0;

    return (
      <Card.Root elevated={false}>
        <Card.Header>
          <Paragraph textStyle="body-bold">Rent payment</Paragraph>
          <Text className="fs-exclude">Due on {formatDate(transaction.dueDate, 'MMMM d, yyyy')}</Text>
        </Card.Header>
        <Card.Body>
          <Box css={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 'tight', columnGap: 'default' }}>
            <Text>Rent amount</Text>
            <Text css={{ textAlign: 'right' }}>{centsToDollars(transaction.amount)}</Text>
            {showCardFee && (
              <>
                <Text>Card fee</Text>
                <Text css={{ textAlign: 'right' }}>{centsToDollars(transaction.zgFee)}</Text>
              </>
            )}
            <Text textStyle="body-bold">Total payment</Text>
            <Text textStyle="body-bold" css={{ textAlign: 'right' }}>
              {centsToDollars(transaction.totalAmount)}
            </Text>
          </Box>
        </Card.Body>
      </Card.Root>
    );
  };

  return (
    <Modal
      size="sm"
      open={true}
      trigger={null}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          goToDashboard();
        }
      }}
      header={<Heading level={2}>Your {formatDate(transaction.dueDate, 'MMMM d')} rent is overdue</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'loose' }}>
          <HelmetContainer {...SeoConstants.payPastDueRentPaymentPage} />
          <NotificationContainer persona={userPersonas.RENTER} />
          {renderOverdueAlert()}
          <Box>
            <Paragraph textStyle="body-bold">Pay on</Paragraph>
            <Text>{formatDate(today, 'MMM d, yyyy')}</Text>
          </Box>
          {renderRentPaymentSection()}
          <AvailablePaymentMethods
            renderSelect
            onOptionChange={(e) => setSelectedPaymentMethodId(e.target.value)}
            isLoading={isLoading}
            selectedPaymentMethodId={selectedPaymentMethodId}
            description={
              <FormHelp>
                Your payment will be processed immediately. If this date falls on a weekend or holiday, we’ll send the
                payment 1 business day later.
              </FormHelp>
            }
          />
          <PaymentAuthorizationTerms variant="oneTime" />
          <FaqComponent faqs={faqs.PAYMENT_SETTINGS_V2.OVERDUE_SCHEDULING_PAGE.body()} />
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Pay past-due rent">
          <DebouncedButton emphasis="filled" onClick={onSubmit} loading={isLoading}>
            {failedPaymentEmail ? 'Try again' : 'Send rent payment now'}
          </DebouncedButton>
        </ButtonGroup>
      }
    />
  );
};

export default PayPastDueRentPaymentPage;
